import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import { signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { showError } from '../utils/notifications';
import { logLogin } from '../services/auditService'; // Importar servicio de auditoría
import { runFullDiagnostic, logAuthError, checkUserExists } from '../utils/firebaseDiagnostic'; // Importar diagnóstico

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [backgroundIndex, setBackgroundIndex] = useState(0);
  const navigate = useNavigate();

  // Array de fondos dinámicos (gradientes)
  const backgrounds = [
    'bg-gradient-to-r from-blue-500 to-purple-600',
    'bg-gradient-to-r from-green-400 to-blue-500',
    'bg-gradient-to-r from-purple-500 to-pink-500',
    'bg-gradient-to-r from-yellow-400 to-red-500',
    'bg-gradient-to-r from-indigo-500 to-purple-500'
  ];

  // Cambiar el fondo al cargar el componente
  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * backgrounds.length);
    setBackgroundIndex(randomIndex);
  }, [backgrounds.length]); // Added the missing dependency

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      // Either remove the user variable or use it
      if (currentUser) {
        let storedRole = '';
        try { storedRole = localStorage.getItem('userRole') || sessionStorage.getItem('userRole') || ''; } catch (_) {}
        if (storedRole === 'Monitoreo') {
          navigate('/ticket-search');
        } else {
          navigate('/dashboard');
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null); // Limpiar errores previos
    
    try {
      console.log("Iniciando sesión con email:", email);
      
      // Autenticar con Firebase Auth
      await signInWithEmailAndPassword(auth, email, password);
      
      console.log("Autenticación exitosa, verificando datos de usuario en Firestore");
      
      // Verificar si el usuario existe en la colección 'usuarios'
      const userDoc = await getDoc(doc(db, 'usuarios', email));
      
      if (userDoc.exists()) {
        // Usuario encontrado en la colección 'usuarios'
        const userData = userDoc.data();
        
        console.log("Datos del usuario encontrados:", userData);
        console.log("Rol del usuario:", userData.rol);
          // Verificar que el usuario esté activo
        if (userData.status !== 1) {
          console.log("Usuario inactivo, status:", userData.status);
          // Registrar intento fallido en logs de auditoría
          await logLogin({...userData, email}, {
            success: false,
            reason: 'cuenta_inactiva',
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            timestamp: new Date().toISOString()
          });
          await auth.signOut();
          showError("Cuenta inactiva", "Tu cuenta está desactivada. Contacta al administrador.");
          return;
        }          // Verificar si el rol tiene acceso al panel de administración
        if (userData.rol !== 'Admin' && userData.rol !== 'Supervisor' && userData.rol !== 'Recaudador' && userData.rol !== 'Auditor' && userData.rol !== 'Monitoreo' && userData.rol !== 'Financiero') {
          console.log("Acceso denegado. El rol", userData.rol, "no tiene permiso para acceder al panel de administración");
          // Registrar intento fallido en logs de auditoría
          await logLogin({...userData, email}, {
            success: false,
            reason: 'rol_sin_permisos',
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            timestamp: new Date().toISOString()
          });
          await auth.signOut();
          showError("Acceso denegado", "No tienes permisos para acceder al panel de administración. Este panel es exclusivo para supervisores, administradores y recaudadores.");
          return;
        }
        
        // Guardar información del rol en localStorage para persistencia
        try {
          localStorage.setItem('userRole', userData.rol || 'INVITADO');
          localStorage.setItem('userName', userData.nombre || 'Usuario');
          localStorage.setItem('userEmail', userData.email);
        } catch (_) {}
        try {
          sessionStorage.setItem('userRole', userData.rol || 'INVITADO');
          sessionStorage.setItem('userName', userData.nombre || 'Usuario');
          sessionStorage.setItem('userEmail', userData.email);
        } catch (_) {}
        
        console.log("Información guardada en localStorage y sessionStorage:");
        console.log("- Rol:", userData.rol);
        console.log("- Nombre:", userData.nombre);
        console.log("- Email:", userData.email);
          // Registrar inicio de sesión exitoso en logs de auditoría
        await logLogin({...userData, email}, {
          success: true,
          loginMethod: 'email',
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          timestamp: new Date().toISOString()
        });
        
        // Forzar recarga de la página para asegurar que los permisos se apliquen correctamente
        if (userData.rol === 'Supervisor') {
          console.log("Usuario Supervisor detectado, redirigiendo a Controladores Autorizados");
          window.location.href = '/vendors';
        } else if (userData.rol === 'Monitoreo') {
          console.log("Usuario Monitoreo detectado, redirigiendo a Consultar Tickets");
          window.location.href = '/ticket-search';
        } else {
          // Redirigir normalmente para otros roles (Admin)
          console.log("Redirigiendo al dashboard");
          navigate('/dashboard');
        }
      } else {        // Usuario no encontrado en la colección 'usuarios'
        console.log("¡Error! Usuario no encontrado en Firestore para el email:", email);
        
        // Ejecutar diagnóstico para ayudar con el debug
        console.log("🔍 === EJECUTANDO DIAGNÓSTICO POR USUARIO NO ENCONTRADO ===");
        await checkUserExists(email);
        
        // Registrar intento fallido en logs de auditoría
        await logLogin({email}, {
          success: false,
          reason: 'usuario_no_encontrado',
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          timestamp: new Date().toISOString()
        });
        await auth.signOut(); // Cerrar la sesión de autenticación
        showError("Acceso denegado", "Tu cuenta no está registrada en el sistema.");
      }    } catch (error) {
      console.error("Error durante el inicio de sesión:", error);
      
      // Ejecutar log detallado de error
      logAuthError(error, email);
      
      // Mejorar manejo de errores específicos
      let errorTitle = "Error de inicio de sesión";
      let errorMessage = "Verifica tus credenciales e intenta nuevamente.";
      
      switch (error.code) {
        case 'auth/invalid-credential':
          errorTitle = "Credenciales incorrectas";
          errorMessage = `❌ El email "${email}" o la contraseña son incorrectos.\n\n🔍 Verifica que:\n• El email esté escrito correctamente\n• La contraseña sea la correcta\n• La cuenta esté registrada en el sistema`;
          
          // Ejecutar diagnóstico adicional para credenciales inválidas
          console.log("🔍 === EJECUTANDO DIAGNÓSTICO POR CREDENCIALES INVÁLIDAS ===");
          await runFullDiagnostic(email);
          break;
        case 'auth/user-not-found':
          errorTitle = "Usuario no encontrado";
          errorMessage = `❌ No existe una cuenta con el email "${email}".\n\n👤 Contacta al administrador para verificar tu registro.`;
          break;
        case 'auth/wrong-password':
          errorTitle = "Contraseña incorrecta";
          errorMessage = "🔑 La contraseña ingresada es incorrecta. Intenta nuevamente.";
          break;
        case 'auth/invalid-email':
          errorTitle = "Email inválido";
          errorMessage = "📧 El formato del email no es válido. Verifica que esté escrito correctamente.";
          break;
        case 'auth/user-disabled':
          errorTitle = "Cuenta deshabilitada";
          errorMessage = "🚫 Esta cuenta ha sido deshabilitada. Contacta al administrador.";
          break;
        case 'auth/too-many-requests':
          errorTitle = "Demasiados intentos";
          errorMessage = "⏰ Demasiados intentos fallidos. Espera unos minutos antes de intentar nuevamente.";
          break;
        case 'auth/network-request-failed':
          errorTitle = "Error de conexión";
          errorMessage = "🌐 Error de conexión. Verifica tu conexión a internet e intenta nuevamente.";
          break;
        default:
          errorMessage = `❌ Error: ${error.message}\n\n📧 Si el problema persiste, contacta al soporte técnico.`;
      }
      
      // Registrar intento fallido en logs de auditoría
      await logLogin({email}, {
        success: false,
        reason: error.code || 'error_desconocido',
        error: error.message,
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        timestamp: new Date().toISOString()
      });
      
      // Mostrar error específico
      showError(errorTitle, errorMessage);
      setError(''); // Limpiar el error del estado ya que usamos SweetAlert
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center ${backgrounds[backgroundIndex]}`}>
      <div className="bg-white p-8 rounded-lg shadow-md w-96">
        <h2 className="text-2xl font-bold mb-3 text-center">Parqueo Amigo</h2>
        <p className="text-gray-600 text-center mb-6">
          Sistema de administración para gestión inteligente de estacionamientos
        </p>
        
        {/* Botón de diagnóstico temporal */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-xs text-yellow-800 mb-2">🔧 Herramientas de Desarrollo:</p>
            <button
              type="button"
              onClick={() => runFullDiagnostic('taxiappec@gmail.com')}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-1 rounded text-sm mb-2"
            >
              🔍 Diagnosticar Usuario: taxiappec@gmail.com
            </button>
            <button
              type="button"
              onClick={() => runFullDiagnostic()}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white py-1 rounded text-sm"
            >
              🔍 Diagnóstico General de Firebase
            </button>
          </div>
        )}
        
        {error && <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>}
        <form onSubmit={handleLogin}>
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded"
              required
            />
          </div>
          <div className="mb-6">
            <label className="block text-gray-700 mb-2" htmlFor="password">Contraseña</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded"
              required
            />
          </div>
          <button
            type="submit"
            className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 rounded transition-colors duration-300"
          >
            Iniciar Sesión
          </button>
        </form>
        <p className="mt-4 text-sm text-center text-gray-500">
          Panel exclusivo para administradores de Parqueo Amigo
        </p>
      </div>
    </div>
  );
}

export default Login;
